import { EMPTY_ARRAY } from '#pplx/frontend/common/defaults.js';
import { assertUnreachableValue } from '#pplx/frontend/common/type_utils/unreachableValue.js';
import { getNextTypeaheadIndex } from '#pplx/frontend/composer-sdk/typeahead/getNextTypeaheadIndex.js';
import { mergeRegister } from '@lexical/utils';
import { COMMAND_PRIORITY_LOW, KEY_ENTER_COMMAND, KEY_TAB_COMMAND, SKIP_DOM_SELECTION_TAG, } from 'lexical';
import { useCallback, useEffect, useRef, useState } from 'react';
/** Selects the currently displayable rows from the shared overlay state. */
export const rowsOf = (state) => state.status === 'done' || state.status === 'loading' ? state.rows : [];
/** Dispatches a synchronous selection and its matching cleanup. */
export function useDispatchTypeaheadSelection({ editor, onSelection, onClose, onHandoff, }) {
    return useCallback((...args) => {
        const selection = onSelection(...args);
        if (selection === null)
            return;
        const outcome = { kind: selection.outcome ?? 'close' };
        switch (outcome.kind) {
            case 'close':
                onClose(selection);
                return;
            case 'handoff':
                editor.update(() => onHandoff(selection), { tag: SKIP_DOM_SELECTION_TAG });
                return;
            default:
                assertUnreachableValue(outcome);
        }
    }, [editor, onSelection, onClose, onHandoff]);
}
export function shouldPreselectFirstItem(initialSelection = 'first') {
    switch (initialSelection) {
        case 'first':
            return true;
        case 'none':
            return false;
        default:
            assertUnreachableValue(initialSelection);
    }
}
/** Keeps provider overrides and omitted surface defaults out of the shared edge calculation. */
export function resolveTypeaheadEdges({ key, wrap, above, enter = true, navigation, }) {
    let edges;
    switch (navigation) {
        case 'wrap':
            edges = { inputEdge: 'wrap', farEdge: 'wrap' };
            break;
        case 'clamp':
            edges = { inputEdge: 'clamp', farEdge: 'clamp' };
            break;
        case 'return-to-input':
            edges = { inputEdge: 'release', farEdge: 'wrap' };
            break;
        case undefined:
            // Existing above-input menus always release at the input edge, even when wrapping.
            edges = { inputEdge: above || !wrap ? 'release' : 'wrap', farEdge: wrap ? 'wrap' : 'clamp' };
            break;
        default:
            return assertUnreachableValue(navigation);
    }
    return {
        ...edges,
        // Only return-to-input reserves the below-input arrow for native caret movement.
        enter: navigation === 'return-to-input'
            ? enter && (above ? key === 'ArrowUp' : key === 'ArrowDown')
            : !above || enter,
    };
}
/** Reads whether the collapsed DOM caret is on the first rendered editor line. */
export function isCaretOnFirstVisualLine(editor) {
    const domSelection = window.getSelection();
    if (!domSelection?.isCollapsed)
        return !domSelection;
    const rootElement = editor.getRootElement();
    if (!domSelection.rangeCount || !rootElement?.contains(domSelection.anchorNode)) {
        return true;
    }
    const caretRect = domSelection.getRangeAt(0).getBoundingClientRect();
    const firstLineRect = rootElement.firstElementChild?.getBoundingClientRect();
    return (!caretRect.height || !firstLineRect?.height || Math.abs(caretRect.top - firstLineRect.top) <= 4);
}
export function moveTypeaheadHighlight({ editor, index, count, key, wrap, above, onMove, navigation, }) {
    const nextIndex = getNextTypeaheadIndex({
        index,
        count,
        key,
        above,
        ...resolveTypeaheadEdges({
            key,
            wrap,
            above,
            enter: !above || index !== null || isCaretOnFirstVisualLine(editor),
            navigation,
        }),
    });
    if (nextIndex === undefined)
        return null;
    onMove(nextIndex);
    return true;
}
function useSelectedIndex() {
    const [selectedIndex, setSelectedIndexState] = useState(null);
    // State renders the highlight; the ref gives long-lived keyboard handlers the latest index.
    const selectedIndexRef = useRef(null);
    const setSelectedIndex = useCallback((nextIndex = null) => {
        setSelectedIndexState(nextIndex);
        selectedIndexRef.current = nextIndex;
    }, []);
    return [selectedIndex, selectedIndexRef, setSelectedIndex];
}
/**
 * Highlight and keyboard navigation shared by the Composer-owned overlay paths
 * (input-anchored and marker-anchored), which don't mount `LexicalMenu` and so
 * own their row state directly. Owns the highlighted index, first-row
 * highlighting when results arrive, and the Arrow/Enter/Tab handlers; each
 * path keeps its own close keys (Escape, the marker path's Backspace).
 */
export function useTypeaheadListNavigation({ editor, enabled, activeRef, state, initialSelection, wrapNavigation, navigation, }) {
    const [selectedIndex, selectedIndexRef, setSelectedIndex] = useSelectedIndex();
    const rowsRef = useRef(EMPTY_ARRAY);
    const selectRowRef = useRef(() => undefined);
    // Keep the out-of-render handlers pointed at the current rows.
    useEffect(() => {
        const rows = rowsOf(state);
        rowsRef.current = rows;
        if (selectedIndexRef.current !== null && selectedIndexRef.current >= rows.length) {
            setSelectedIndex(null);
        }
        // Providers can leave Enter available for surface submission until the user highlights a row.
        // Result updates preserve an existing highlight regardless of the initial selection policy.
        if (activeRef.current &&
            selectedIndexRef.current === null &&
            rows.length > 0 &&
            shouldPreselectFirstItem(initialSelection)) {
            setSelectedIndex(0);
        }
    }, [state, activeRef, initialSelection, selectedIndexRef, setSelectedIndex]);
    const onArrowKey = useCallback((key, upward) => {
        if (!activeRef.current)
            return false;
        return moveTypeaheadHighlight({
            editor,
            index: selectedIndexRef.current,
            count: rowsRef.current.length,
            key,
            wrap: wrapNavigation,
            above: upward,
            onMove: setSelectedIndex,
            navigation,
        });
    }, [editor, activeRef, selectedIndexRef, setSelectedIndex, wrapNavigation, navigation]);
    // Enter with nothing highlighted falls through so the surface's own submit
    // (e.g. the command palette's activate-first-result) handles it.
    const selectHighlightedRow = useCallback((event) => {
        if (!activeRef.current || selectedIndexRef.current === null)
            return false;
        event?.preventDefault();
        selectRowRef.current(selectedIndexRef.current, 'keyboard');
        return true;
    }, [activeRef, selectedIndexRef]);
    useEffect(() => {
        if (!enabled)
            return;
        return mergeRegister(editor.registerCommand(KEY_ENTER_COMMAND, selectHighlightedRow, COMMAND_PRIORITY_LOW), editor.registerCommand(KEY_TAB_COMMAND, selectHighlightedRow, COMMAND_PRIORITY_LOW));
    }, [editor, enabled, selectHighlightedRow]);
    return {
        selectedIndex,
        selectedIndexRef,
        rowsRef,
        selectRowRef,
        onHighlight: setSelectedIndex,
        onArrowKey,
        resetSelection: setSelectedIndex,
    };
}
//# sourceMappingURL=useTypeaheadListNavigation.js.map